import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
} from "@mui/material";
import deliveryAgentsApi from "../../api/delivery-agents";
import Layout from "../../components/layout/Layout";
import Swal from "sweetalert2";

interface PendingRider {
  _id: string;
  name: string;
  email: string;
  phoneNumber: string;
  createdAt: string;
  approvalStatus: string;
  bikeNumber?: string;
  aadharNumber?: string;
  alternatePhoneNumber?: string;
  address?: string;
}

const RiderRequests: React.FC = () => {
  const [riders, setRiders] = useState<PendingRider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPendingRiders();
  }, []);

  const fetchPendingRiders = async () => {
    setLoading(true);
    try {
      const data = await deliveryAgentsApi.getPendingRiders();
      // Expecting { success: true, pendingRiders: [...] }
      setRiders(data.pendingRiders || []);
    } catch (err: any) {
      setError(err.response?.data?.message || "Error fetching pending rider requests");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (riderId: string, name: string) => {
    const result = await Swal.fire({
      title: `Approve rider request?`,
      text: `Approved riders will immediately gain access to batches and orders.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Approve",
      confirmButtonColor: "#10B981",
      cancelButtonColor: "#64748B",
    });

    if (result.isConfirmed) {
      try {
        await deliveryAgentsApi.approveOrRejectRider(riderId, "approved");
        Swal.fire({
          title: "Approved!",
          text: `Rider ${name || ""} has been approved.`,
          icon: "success",
          confirmButtonColor: "#f97316",
        });
        fetchPendingRiders();
      } catch (err: any) {
        Swal.fire({
          title: "Error",
          text: err.response?.data?.message || "Failed to approve rider",
          icon: "error",
        });
      }
    }
  };

  const handleReject = async (riderId: string, name: string) => {
    const result = await Swal.fire({
      title: `Reject rider request?`,
      text: `Rejected riders will be blocked from accessing the system.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Reject",
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#64748B",
    });

    if (result.isConfirmed) {
      try {
        await deliveryAgentsApi.approveOrRejectRider(riderId, "rejected");
        Swal.fire({
          title: "Rejected!",
          text: `Rider ${name || ""} has been rejected.`,
          icon: "success",
          confirmButtonColor: "#f97316",
        });
        fetchPendingRiders();
      } catch (err: any) {
        Swal.fire({
          title: "Error",
          text: err.response?.data?.message || "Failed to reject rider",
          icon: "error",
        });
      }
    }
  };

  return (
    <Layout>
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" fontWeight={700} color="secondary.main">
            Rider Requests
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Manage delivery rider onboarding requests. Pending riders must be approved before they can receive orders.
          </Typography>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name / Email</TableCell>
                <TableCell>Phone Number</TableCell>
                <TableCell>Bike & Aadhar</TableCell>
                <TableCell>Alt Phone & Address</TableCell>
                <TableCell>Request Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    Loading pending requests...
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: "error.main" }}>
                    {error}
                  </TableCell>
                </TableRow>
              ) : riders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    No pending rider requests found
                  </TableCell>
                </TableRow>
              ) : (
                riders.map((rider) => (
                  <TableRow key={rider._id} sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}>
                    <TableCell>
                      <Typography fontWeight={700}>{rider.name || "Unnamed Rider"}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {rider.email || "No email"}
                      </Typography>
                    </TableCell>
                    <TableCell>{rider.phoneNumber}</TableCell>
                    <TableCell>
                      <Typography variant="body2"><strong>Bike:</strong> {rider.bikeNumber || "N/A"}</Typography>
                      <Typography variant="body2"><strong>Aadhar:</strong> {rider.aadharNumber || "N/A"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2"><strong>Alt Phone:</strong> {rider.alternatePhoneNumber || "N/A"}</Typography>
                      <Typography variant="body2" sx={{ maxWidth: 200, whiteSpace: "normal" }}>
                        <strong>Address:</strong> {rider.address || "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {new Date(rider.createdAt).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label="PENDING APPROVAL"
                        size="small"
                        color="warning"
                        sx={{ fontSize: "0.675rem", fontWeight: 800 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          onClick={() => handleApprove(rider._id, rider.name)}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => handleReject(rider._id, rider.name)}
                        >
                          Reject
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Layout>
  );
};

export default RiderRequests;
